import { Check } from "lucide-react";
import { BOOK_URL } from "@/lib/site";
import Reveal from "./Reveal";

const plans = [
  {
    tag: "Option 01",
    name: "No Money Down",
    upfront: "$0",
    upfrontLabel: "upfront",
    repay: "$55",
    terms: "per month · 48 month terms",
    featured: false,
  },
  {
    tag: "Option 02",
    name: "Monthly Installment",
    upfront: "$1,000",
    upfrontLabel: "upfront",
    repay: "$70",
    terms: "per month · 24 month terms",
    featured: true,
  },
  {
    tag: "Option 03",
    name: "Buy Outright",
    upfront: "$2,640",
    upfrontLabel: "one-off",
    repay: "$0",
    terms: "No repayments",
    featured: false,
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative bg-[#070707] py-28 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#169ED9]">
              / Pricing
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              No Guesswork. No Hidden Surprises.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-white/55 leading-relaxed max-w-xl">
              Our pricing is fully inclusive with three flexible payment options to
              suit your needs. Choose what works for you.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-4">
          {plans.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <div
                data-testid={`pricing-card-${i}`}
                className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border p-8 lg:p-10 transition-colors duration-500 ${
                  p.featured
                    ? "border-[#169ED9]/50 bg-[#169ED9]/[0.04]"
                    : "border-white/10 bg-[#0a0a0a] hover:border-white/25"
                }`}
              >
                {p.featured && (
                  <span className="absolute right-6 top-6 font-mono text-[10px] uppercase tracking-[0.2em] text-black bg-[#169ED9] px-2.5 py-1 rounded-full">
                    Popular
                  </span>
                )}
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">
                  {p.tag}
                </span>
                <h3 className="mt-4 font-heading font-bold text-2xl tracking-tight">
                  {p.name}
                </h3>

                <div className="mt-8 flex items-end gap-2">
                  <span className="font-heading font-black tracking-tighter text-5xl lg:text-6xl">
                    {p.upfront}
                  </span>
                  <span className="mb-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/40">
                    {p.upfrontLabel}
                  </span>
                </div>

                <div className="mt-6 border-t border-white/10 pt-6">
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`font-heading font-bold text-2xl ${
                        p.repay === "$0" ? "text-white/30" : "text-[#169ED9]"
                      }`}
                    >
                      {p.repay}
                    </span>
                  </div>
                  <p className="mt-1 text-white/50 text-sm">{p.terms}</p>
                  <p className="mt-3 text-white/35 text-xs">** Pricing for standard install.</p>
                </div>

                <ul className="mt-8 space-y-3 flex-1">
                  {[
                    "Supply & installation included",
                    "Free onsite assessment",
                    "Fully licensed & insured",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-white/60">
                      <Check className="h-4 w-4 shrink-0 text-[#169ED9] mt-0.5" strokeWidth={2} />
                      {t}
                    </li>
                  ))}
                </ul>

                <a
                  href={BOOK_URL}
                  data-testid={`pricing-book-btn-${i}`}
                  className={`mt-10 inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-semibold tracking-wide transition-colors duration-300 ${
                    p.featured
                      ? "bg-[#169ED9] text-black hover:bg-white"
                      : "border border-white/20 text-white hover:bg-white hover:text-black"
                  }`}
                >
                  Book Assessment
                  <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 rounded-2xl border border-white/10 bg-[#0a0a0a] p-8 lg:p-10">
            <h4 className="font-heading font-bold text-xl tracking-tight text-[#169ED9]">
              Free Onsite Assessment
            </h4>
            <p className="mt-3 text-white/55 leading-relaxed max-w-3xl">
              Before any work begins, we provide a free onsite inspection and quotation
              to confirm your property&apos;s suitability and ensure the valve is the right
              solution for your specific situation.
            </p>
            <p className="mt-4 text-white/35 text-xs leading-relaxed max-w-3xl">
              *Pricing is subject to the free onsite visit to confirm the job is
              achievable and within our pricing model. Finance repayments are subject to
              meeting the vendor&apos;s requirements.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
